import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
    FileText, Download, Users, Calendar, DollarSign, 
    Palmtree, ClipboardList, TrendingDown, RefreshCw, Printer
} from 'lucide-react';
import logger from '@/lib/logger';

const REPORT_TYPES = [
    { id: 'headcount', label: 'Headcount Report', icon: Users, desc: 'Current employee count by department, type, status, location, and gender' },
    { id: 'attendance_summary', label: 'Attendance Summary', icon: Calendar, desc: 'Monthly attendance breakdown per employee' },
    { id: 'payroll_summary', label: 'Payroll Summary', icon: DollarSign, desc: 'Monthly payroll costs by department' },
    { id: 'leave_balance', label: 'Leave Balance', icon: Palmtree, desc: 'Annual/sick leave used vs remaining per employee' },
    { id: 'employee_directory', label: 'Employee Directory', icon: ClipboardList, desc: 'Complete employee contact and employment listing' },
    { id: 'turnover', label: 'Turnover Analysis', icon: TrendingDown, desc: 'New hires, exits, and turnover rate for the year' },
];

const MONTHS = [];
const now = new Date();
for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    MONTHS.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
}

const formatCurrency = (amount) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(amount || 0);

const HRReportsPage = () => {
    const [selectedReport, setSelectedReport] = useState(null);
    const [department, setDepartment] = useState('all');
    const [month, setMonth] = useState(MONTHS[0]?.value || '');
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const generateReport = async () => {
        if (!selectedReport) {
            toast.error('Please select a report type');
            return;
        }
        setLoading(true);
        try {
            const params = new URLSearchParams({ report_type: selectedReport });
            if (department && department !== 'all') params.append('department', department);
            if (['attendance_summary', 'payroll_summary'].includes(selectedReport) && month) {
                params.append('month', month);
            }
            const res = await api.get(`/hr/reports/generate?${params.toString()}`);
            setReportData(res.data);
            toast.success('Report generated');
        } catch (error) {
            logger.error('Report error:', error);
            toast.error(error.response?.data?.detail || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => window.print();

    const renderReport = () => {
        if (!reportData) return null;
        const rt = reportData.report_type;

        if (rt === 'headcount') {
            return (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card><CardContent className="pt-4 text-center"><p className="text-3xl font-bold">{reportData.total}</p><p className="text-xs text-muted-foreground">Total Employees</p></CardContent></Card>
                        {Object.entries(reportData.by_location || {}).map(([loc, count]) => (
                            <Card key={loc}><CardContent className="pt-4 text-center"><p className="text-3xl font-bold">{count}</p><p className="text-xs text-muted-foreground">{loc}</p></CardContent></Card>
                        ))}
                    </div>
                    <Card>
                        <CardHeader><CardTitle className="text-sm">By Department</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {Object.entries(reportData.by_department || {}).map(([dept, count]) => (
                                    <div key={dept} className="flex justify-between p-2 rounded bg-muted/40 text-sm">
                                        <span>{dept}</span><span className="font-bold">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle className="text-sm">By Gender</CardTitle></CardHeader>
                        <CardContent>
                            <div className="flex gap-4">
                                {Object.entries(reportData.by_gender || {}).map(([g, count]) => (
                                    <Badge key={g} variant="outline" className="text-sm py-1 px-3">{g}: {count}</Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        if (rt === 'attendance_summary') {
            return (
                <Card>
                    <CardHeader><CardTitle className="text-sm">Attendance Summary — {reportData.month}</CardTitle></CardHeader>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Department</TableHead>
                                <TableHead className="text-center">Present</TableHead>
                                <TableHead className="text-center">Late</TableHead>
                                <TableHead className="text-center">Absent</TableHead>
                                <TableHead className="text-center">Half Day</TableHead>
                                <TableHead className="text-center">WFH</TableHead>
                                <TableHead className="text-center">Leave</TableHead>
                                <TableHead className="text-right">Hours</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(reportData.employees || []).map((e, i) => (
                                <TableRow key={e.employee_id || e.employee_name || i}>
                                    <TableCell className="font-medium">{e.employee_name}</TableCell>
                                    <TableCell>{e.department}</TableCell>
                                    <TableCell className="text-center text-emerald-600 font-medium">{e.present}</TableCell>
                                    <TableCell className="text-center text-amber-600">{e.late || 0}</TableCell>
                                    <TableCell className="text-center text-red-600">{e.absent || 0}</TableCell>
                                    <TableCell className="text-center text-orange-500">{e.half_day || 0}</TableCell>
                                    <TableCell className="text-center text-blue-500">{e.wfh || 0}</TableCell>
                                    <TableCell className="text-center text-violet-600">{e.leave || 0}</TableCell>
                                    <TableCell className="text-right">{(e.total_hours || 0).toFixed(1)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            );
        }

        if (rt === 'payroll_summary') {
            return (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{reportData.total_employees}</p><p className="text-xs text-muted-foreground">Employees</p></CardContent></Card>
                        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{formatCurrency(reportData.total_gross)}</p><p className="text-xs text-muted-foreground">Total Gross</p></CardContent></Card>
                        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{formatCurrency(reportData.total_deductions)}</p><p className="text-xs text-muted-foreground">Deductions</p></CardContent></Card>
                        <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-emerald-600">{formatCurrency(reportData.total_net)}</p><p className="text-xs text-muted-foreground">Net Payable</p></CardContent></Card>
                    </div>
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Department Breakdown</CardTitle></CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Department</TableHead>
                                    <TableHead className="text-center">Employees</TableHead>
                                    <TableHead className="text-right">Gross</TableHead>
                                    <TableHead className="text-right">Deductions</TableHead>
                                    <TableHead className="text-right">Net</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {Object.entries(reportData.by_department || {}).map(([dept, info]) => (
                                    <TableRow key={dept}>
                                        <TableCell className="font-medium">{dept}</TableCell>
                                        <TableCell className="text-center">{info.count}</TableCell>
                                        <TableCell className="text-right">{formatCurrency(info.gross)}</TableCell>
                                        <TableCell className="text-right text-red-600">{formatCurrency(info.deductions)}</TableCell>
                                        <TableCell className="text-right font-bold">{formatCurrency(info.net)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            );
        }

        if (rt === 'leave_balance') {
            return (
                <Card>
                    <CardHeader><CardTitle className="text-sm">Leave Balance — {reportData.year}</CardTitle></CardHeader>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Department</TableHead>
                                <TableHead className="text-center">Annual Entitlement</TableHead>
                                <TableHead className="text-center">Annual Used</TableHead>
                                <TableHead className="text-center">Annual Balance</TableHead>
                                <TableHead className="text-center">Sick Used</TableHead>
                                <TableHead className="text-center">Sick Balance</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(reportData.employees || []).map((e, i) => (
                                <TableRow key={e.employee_id || e.employee_name || i}>
                                    <TableCell className="font-medium">{e.employee_name}</TableCell>
                                    <TableCell>{e.department}</TableCell>
                                    <TableCell className="text-center">{e.annual_entitlement}</TableCell>
                                    <TableCell className="text-center text-amber-600">{e.annual_used}</TableCell>
                                    <TableCell className="text-center font-bold">{e.annual_remaining}</TableCell>
                                    <TableCell className="text-center text-amber-600">{e.sick_used}</TableCell>
                                    <TableCell className="text-center font-bold">{e.sick_remaining}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            );
        }

        if (rt === 'employee_directory') {
            return (
                <Card>
                    <CardHeader><CardTitle className="text-sm">Employee Directory ({reportData.total} employees)</CardTitle></CardHeader>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Department</TableHead>
                                <TableHead>Designation</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Joined</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(reportData.employees || []).map((e, i) => (
                                <TableRow key={e.employee_id || e.employee_name || i}>
                                    <TableCell className="font-mono text-xs">{e.employee_id}</TableCell>
                                    <TableCell className="font-medium">{e.full_name}</TableCell>
                                    <TableCell>{e.department}</TableCell>
                                    <TableCell>{e.designation}</TableCell>
                                    <TableCell className="text-xs">{e.company_email || '-'}</TableCell>
                                    <TableCell className="text-xs">{e.mobile_number || '-'}</TableCell>
                                    <TableCell className="text-xs">{e.joining_date || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            );
        }

        if (rt === 'turnover') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-emerald-600">{reportData.new_hires}</p><p className="text-sm text-muted-foreground">New Hires</p></CardContent></Card>
                    <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-red-600">{reportData.exits}</p><p className="text-sm text-muted-foreground">Exits</p></CardContent></Card>
                    <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold">{reportData.current_headcount}</p><p className="text-sm text-muted-foreground">Current Headcount</p></CardContent></Card>
                    <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-amber-600">{reportData.turnover_rate}%</p><p className="text-sm text-muted-foreground">Turnover Rate</p></CardContent></Card>
                </div>
            );
        }

        return <p className="text-muted-foreground">Report type not recognized</p>;
    };

    return (
        <div className="space-y-6" data-testid="hr-reports-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">HR Reports</h1>
                    <p className="text-sm text-muted-foreground">Generate and download comprehensive HR reports</p>
                </div>
                {reportData && (
                    <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-btn">
                        <Printer className="h-4 w-4 mr-2" />Print
                    </Button>
                )}
            </div>

            {/* Report Selection */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {REPORT_TYPES.map(rt => {
                    const Icon = rt.icon;
                    const isSelected = selectedReport === rt.id;
                    return (
                        <button
                            key={rt.id}
                            onClick={() => setSelectedReport(rt.id)}
                            className={`p-4 rounded-lg border text-left transition-all ${
                                isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'
                            }`}
                            data-testid={`report-type-${rt.id}`}
                        >
                            <Icon className={`h-5 w-5 mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                            <p className="font-medium text-sm">{rt.label}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{rt.desc}</p>
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            {selectedReport && (
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-end gap-4 flex-wrap">
                            <div className="space-y-1">
                                <Label className="text-xs">Department</Label>
                                <Select value={department} onValueChange={setDepartment}>
                                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Departments</SelectItem>
                                        <SelectItem value="Sales">Sales</SelectItem>
                                        <SelectItem value="Finance">Finance</SelectItem>
                                        <SelectItem value="Customer Service">Customer Service</SelectItem>
                                        <SelectItem value="Mentors/Academics">Mentors/Academics</SelectItem>
                                        <SelectItem value="Operations">Operations</SelectItem>
                                        <SelectItem value="Marketing">Marketing</SelectItem>
                                        <SelectItem value="HR">HR</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {['attendance_summary', 'payroll_summary'].includes(selectedReport) && (
                                <div className="space-y-1">
                                    <Label className="text-xs">Month</Label>
                                    <Select value={month} onValueChange={setMonth}>
                                        <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {MONTHS.map(m => (
                                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <Button onClick={generateReport} disabled={loading} data-testid="generate-report-btn">
                                {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                                Generate Report
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Report Output */}
            {reportData && (
                <div className="print:p-4" data-testid="report-output">
                    {renderReport()}
                </div>
            )}
        </div>
    );
};

export default HRReportsPage;
